// frontend/src/pages/admin/CreateAccount.js
//
// Back-office account creation. Suppliers were removed from the public signup
// dropdown, which left no way to onboard one at all — this is the replacement
// path, and it works for any partner role.
//
// The admin never chooses a password. The account is created and the partner
// receives an invitation link to set their own, which also confirms the address
// is real before they can sign in.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../api/baseURL';

// Labels only — the values must match the server's enum exactly. The list of
// roles is fetched from the server so this cannot drift from its allowlist.
const ROLE_LABELS = {
    'Spare Parts Supplier': 'Fournisseur de pièces',
    'Car Workshop': 'Atelier / Garage',
    'Car Owner': 'Propriétaire de véhicule',
    'Insurance Company': "Compagnie d'assurance",
    'Vehicle Loader': 'Dépanneur / Remorqueur',
};

// Which roles are businesses. These get a business name and no "Particulier"
// option, matching how the public signup form treats them.
const COMPANY_ROLES = ['Spare Parts Supplier', 'Car Workshop', 'Insurance Company'];

const EMPTY = {
    role: 'Spare Parts Supplier',
    name: '',
    businessName: '',
    email: '',
    phoneNumber: '',
    address: '',
    accountType: 'Company',
};

const CreateAccount = () => {
    const { token } = useAuth();
    const [form, setForm] = useState(EMPTY);
    const [roles, setRoles] = useState(Object.keys(ROLE_LABELS));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [created, setCreated] = useState(null);

    useEffect(() => {
        axios.get(`${API_URL}/admin/creatable-roles`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => { if (res.data?.data?.length) setRoles(res.data.data); })
            // A failed fetch is not worth blocking the form: fall back to the
            // local list, and the server rejects anything it does not allow.
            .catch(() => { });
    }, [token]);

    const isCompany = COMPANY_ROLES.includes(form.role);

    const update = (field) => (e) => {
        const value = e.target.value;
        setForm((f) => ({
            ...f,
            [field]: value,
            // Businesses are always companies; changing role must not leave a
            // stale "Individual" behind.
            ...(field === 'role' && COMPANY_ROLES.includes(value) ? { accountType: 'Company' } : {}),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...form,
                // The display name is what the rest of the app shows; for a
                // business that is the business name.
                name: form.name.trim() || form.businessName.trim(),
            };
            const res = await axios.post(`${API_URL}/admin/users`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCreated(res.data.data);
            setForm(EMPTY);
        } catch (err) {
            setError(err.response?.data?.error || 'Création impossible');
        } finally {
            setSaving(false);
        }
    };

    const inputClass = 'w-full bg-slate-800/60 border border-slate-700 rounded-xl px-4 py-2.5 '
        + 'text-white text-sm outline-none focus:border-blue-500 placeholder-slate-500';
    const labelClass = 'block text-sm text-slate-400 mb-1.5';

    return (
        <div className="max-w-2xl mx-auto p-6 space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                    Créer un compte
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                    Le partenaire reçoit une invitation et choisit lui-même son mot de passe.
                </p>
            </div>

            {created && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <p className="text-green-400 font-medium">Compte créé — invitation envoyée</p>
                    <p className="text-sm text-slate-300 mt-1">
                        {created.name} · {created.email} · {ROLE_LABELS[created.role] || created.role}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                        Le lien est valable 7 jours. Vous pouvez le renvoyer depuis la validation des comptes.
                    </p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5 bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
                <div>
                    <label className={labelClass} htmlFor="role">Type de compte</label>
                    <select id="role" value={form.role} onChange={update('role')} className={inputClass} required>
                        {roles.map((r) => (
                            <option key={r} value={r} className="bg-slate-800">{ROLE_LABELS[r] || r}</option>
                        ))}
                    </select>
                </div>

                {isCompany ? (
                    <div>
                        <label className={labelClass} htmlFor="businessName">Nom de l'entreprise</label>
                        <input
                            id="businessName" value={form.businessName} onChange={update('businessName')}
                            className={inputClass} placeholder="Ex. : Abidjan Auto Pièces" required
                        />
                    </div>
                ) : (
                    <div>
                        <label className={labelClass} htmlFor="name">Nom complet</label>
                        <input
                            id="name" value={form.name} onChange={update('name')}
                            className={inputClass} placeholder="Ex. : Kouassi Yao" required
                        />
                    </div>
                )}

                {isCompany && (
                    <div>
                        <label className={labelClass} htmlFor="contact">Nom du contact <span className="text-slate-600">(optionnel)</span></label>
                        <input
                            id="contact" value={form.name} onChange={update('name')}
                            className={inputClass} placeholder="Personne à contacter"
                        />
                    </div>
                )}

                <div>
                    <label className={labelClass} htmlFor="email">Email</label>
                    <input
                        id="email" type="email" value={form.email} onChange={update('email')}
                        className={inputClass} placeholder="contact@entreprise.ci" required
                    />
                    <p className="text-xs text-slate-500 mt-1">L'invitation est envoyée à cette adresse.</p>
                </div>

                <div>
                    <label className={labelClass} htmlFor="phone">Téléphone</label>
                    <input
                        id="phone" value={form.phoneNumber} onChange={update('phoneNumber')}
                        className={inputClass} placeholder="+225 07 00 00 00 00"
                    />
                </div>

                <div>
                    <label className={labelClass} htmlFor="address">Adresse <span className="text-slate-600">(optionnel)</span></label>
                    <input
                        id="address" value={form.address} onChange={update('address')}
                        className={inputClass} placeholder="Quartier, ville"
                    />
                </div>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                        <p className="text-red-400 text-sm">{error}</p>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={saving}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600
                               text-white font-medium rounded-xl transition-all"
                >
                    {saving ? 'Création…' : 'Créer le compte et envoyer l’invitation'}
                </button>
            </form>
        </div>
    );
};

export default CreateAccount;
